import { useEffect, useState, ReactNode, cloneElement, isValidElement } from 'react';
import logo from '../../assets/intro-logo.png';
import { Box } from '@mui/material';
import Zoom from '@mui/material/Zoom';
import { keyframes } from '@emotion/react';
import AuthLayout from '../../layouts/AuthLayout';
import { DataContextProvider } from '../../context/context';
import DashboardLayout from '../../layouts/DashboardLayout';

interface LandingPageProps {
  /** The component to render after intro fades out */
  targetPage: ReactNode;
  /** Whether to use onReady callback injected as prop to targetPage (true) or timeout (false) */
  useOnReadyCallback?: boolean;
  /** Whether to show intro overlay (default: true) */
  showIntro?: boolean;
  /** Timeout in ms before marking page as ready (only used when useOnReadyCallback is false. default: 5000) */
  readyTimeout?: number;
}

const LandingPage = ({
  targetPage,
  useOnReadyCallback = true,
  showIntro = true,
  readyTimeout = 5000
}: LandingPageProps) => {
  const [showIntroOverlay, setShowIntroOverlay] = useState(showIntro);
  const [pageReady, setPageReady] = useState(false);

  const myKeyframe = keyframes`
    0%  { filter:blur(3px);opacity:1  }
    45% { filter:blur(3px) }
    85% { filter:blur(0px)  }
    90% { opacity:1  }
    100% { opacity:0  }
    100% { filter:blur(0px)  }
`;

  const handlePageReady = () => {
    setPageReady(true);
    setTimeout(() => {
      setShowIntroOverlay(false);
    }, 500);
  };

  useEffect(() => {
    if (!useOnReadyCallback && readyTimeout) {
      const timer = setTimeout(() => {
        handlePageReady();
      }, readyTimeout);
      return () => clearTimeout(timer);
    }
  }, [useOnReadyCallback, readyTimeout]);

  return (
    <>
      {showIntroOverlay && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: pageReady ? 'none' : 'auto',
            opacity: pageReady ? 0 : 1,
            transition: 'opacity 0.5s ease-in-out'
          }}
        >
          <AuthLayout>
            <Box>
              <Zoom in={true} style={{ transitionDuration: '5s', opacity: 0 }}>
                <Box
                  component="img"
                  sx={{
                    animation: `${myKeyframe} 5.1s`,
                    height: {
                      xs: '50vh',
                      sm: '50vh',
                      md: '70vh',
                      lg: '85vh',
                      xl: '85vh'
                    }
                  }}
                  alt="logo."
                  src={logo}
                />
              </Zoom>
            </Box>
          </AuthLayout>
        </Box>
      )}

      <DataContextProvider>
        <Box
          sx={{
            opacity: pageReady ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out',
            visibility: pageReady ? 'visible' : 'hidden',
            position: pageReady ? 'relative' : 'fixed',
            top: pageReady ? 'auto' : '-9999px',
            left: pageReady ? 'auto' : '-9999px'
          }}
        >
          <DashboardLayout>
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              {useOnReadyCallback && isValidElement(targetPage)
                ? cloneElement(targetPage, { onReady: handlePageReady })
                : targetPage}
            </Box>
          </DashboardLayout>
        </Box>
      </DataContextProvider>
    </>
  );
};

export default LandingPage;
